import React, { useMemo } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import db from '../../lib/db';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';

const useWorkspaceEntity = (table, id, transformId = null) => {
  const { workspaceId } = useRouteWorkspace();
  return useLiveQuery(
    () => (id && workspaceId ? table.get([workspaceId, transformId ? transformId(id) : id]) : null),
    [id, workspaceId, table, transformId],
  );
};

/**
 * Factory for creating entity node view components.
 * @param {'profile'|'channel'} entityType
 * @param {React.Component} EntityComponent
 * @param {{ extraAttrs?: string[] }} [options]
 */
export const createEntityNodeView = (entityType, EntityComponent, options = {}) => {
  const { extraAttrs = [] } = options;

  // Each entity type gets its own lookup hook and displayName.
  // Adding a new type? Add a case here or this will throw.
  let useEntity;
  let displayName;
  switch (entityType) {
    case 'profile':
      useEntity = (id) => useWorkspaceEntity(db.profiles, id);
      displayName = 'ProfileNodeView';
      break;
    case 'channel':
      useEntity = (id) => useWorkspaceEntity(db.channels, id, String);
      displayName = 'ChannelNodeView';
      break;
    default:
      throw new Error(`createEntityNodeView: unknown entityType "${entityType}"`);
  }

  const NodeView = ({ node }) => {
    const dataTarget = node.attrs.id;
    const entity = useEntity(dataTarget);

    const extraProps = useMemo(() => {
      const props = {};
      extraAttrs.forEach((attr) => {
        props[attr] = node.attrs[attr];
      });
      return props;
    }, [node.attrs]);

    return (
      <span data-node-view-wrapper>
        <EntityComponent {...{ [entityType]: entity }} dataTarget={dataTarget} truncated {...extraProps} />
      </span>
    );
  };

  NodeView.displayName = displayName;

  return NodeView;
};
